import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, AlertCircle } from "lucide-react";
import { ThemeLoader } from "../../shared/components/ThemeLoader";
import { OrchyLogo } from "../ui/OrchyLogo";
import { SEO } from "../SEO";
import { auth } from "../../lib/firebase";

type State = "loading" | "success" | "error" | "no-session";

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<State>("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const user = auth.currentUser;
        // Cross-device: email is verified at Firebase but no session here — guide user to sign in
        if (!user) {
          if (!cancelled) setState("no-session");
          return;
        }

        // Force-reload to pick up the latest emailVerified flag
        await user.reload();
        if (!auth.currentUser?.emailVerified) {
          throw new Error("Email not verified yet. Please click the link in your inbox, then return to this page.");
        }

        // Email verified — create MongoDB account now so login works immediately
        if (!cancelled) {
          try {
            // Force-refresh the token so it carries the latest email_verified flag
            const idToken = await auth.currentUser!.getIdToken(true);
            const provisionRes = await fetch(`${(import.meta.env.VITE_API_URL || "").replace(/\/$/, "")}/auth/firebase`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ idToken }),
            });
            if (!provisionRes.ok) {
              const errBody = await provisionRes.json().catch(() => ({})) as { error?: string };
              throw new Error(errBody.error ?? "Account provisioning failed. Please try signing in.");
            }
          } catch (provisionErr: any) {
            if (!cancelled) {
              setErrorMsg(provisionErr.message || "Could not set up your account. Please sign in manually.");
              setState("error");
            }
            return;
          }
          setState("success");
          setTimeout(() => navigate("/login?signup=success"), 1500);
        }
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err.message || "Verification failed. Please try again.");
          setState("error");
        }
      }
    };

    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <SEO title="Verifying Email" description="Completing your Orchy account setup." noIndex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <OrchyLogo size={28} />
            <h1 className="text-3xl font-bold text-text">Orchy</h1>
          </div>
          <p className="text-secondary">Workflow Orchestration Platform</p>
        </div>

        <div className="bg-surface rounded-lg border border-edge shadow-lg p-8 text-center">
          {state === "loading" && (
            <>
              <ThemeLoader size={40} className="mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text mb-2">Setting up your account…</h2>
              <p className="text-secondary text-sm">Please wait while we complete verification.</p>
            </>
          )}

          {state === "success" && (
            <>
              <CheckCircle className="w-12 h-12 text-brand mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text mb-2">Email verified!</h2>
              <p className="text-secondary text-sm">Your account is ready. Taking you to sign in…</p>
            </>
          )}

          {state === "no-session" && (
            <>
              <CheckCircle className="w-12 h-12 text-brand mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text mb-2">Email verified!</h2>
              <p className="text-secondary text-sm mb-6">
                Your email was confirmed. Please sign in on the device where you created your account, or sign in below.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-2.5 px-4 bg-brand text-on-primary rounded-lg font-medium hover:bg-brand-light transition"
              >
                Sign In
              </button>
            </>
          )}

          {state === "error" && (
            <>
              <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text mb-2">Verification failed</h2>
              <p className="text-secondary text-sm mb-6">{errorMsg}</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate("/signup")}
                  className="w-full py-2.5 px-4 bg-brand text-on-primary rounded-lg font-medium hover:bg-brand-light transition"
                >
                  Back to Sign Up
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="w-full py-2.5 px-4 border border-edge text-text rounded-lg font-medium hover:bg-surface-hover transition"
                >
                  Sign In
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-secondary mt-6">© 2026 Orchy. All rights reserved.</p>
      </div>
    </div>
  );
};
